import React, { useState, useRef } from 'react';
import { Box, IconButton, Typography, Popover, TextField, InputAdornment } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';

const DayPickerContent = ({ currentDate, onDateChange, datePickerOpen, setDatePickerOpen }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
    <IconButton onClick={() => onDateChange(currentDate.subtract(1, 'day'))}>
      <ArrowBackIosNewIcon fontSize="small" />
    </IconButton>
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DatePicker
        open={datePickerOpen}
        onOpen={() => setDatePickerOpen(true)}
        onClose={() => setDatePickerOpen(false)}
        value={currentDate}
        onChange={newValue => {
          onDateChange(newValue);
          setDatePickerOpen(false);
        }}
        renderInput={({ inputRef, inputProps, InputProps }) => (
          <Box
            ref={inputRef}
            {...inputProps}
            sx={{
              mx: 2,
              minWidth: 180,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              borderBottom: '1px dashed #6d7eea'
            }}
            onClick={() => setDatePickerOpen(true)}
          >
            <Typography variant="h6">
              {currentDate.format('MMMM D, YYYY')}
            </Typography>
            {InputProps?.endAdornment}
          </Box>
        )}
      />
    </LocalizationProvider>
    <IconButton onClick={() => onDateChange(currentDate.add(1, 'day'))}>
      <ArrowForwardIosIcon fontSize="small" />
    </IconButton>
  </Box>
);

const getStartOfWeek = (date) => date.startOf('week'); // Sunday
const getEndOfWeek = (date) => date.endOf('week');

function formatWeekRange(start, end) {
  return `${start.format('ddd DD.MM.YYYY')} - ${end.format('ddd DD.MM.YYYY')}`;
}

const WeekPicker = ({ currentDate, onWeekChange }) => {
  const [open, setOpen] = useState(false);
  const [hoveredDate, setHoveredDate] = useState(null);
  const anchorRef = useRef(null);
  const startOfWeek = getStartOfWeek(currentDate);
  const endOfWeek = getEndOfWeek(currentDate);
  const startOfHoveredWeek = hoveredDate ? getStartOfWeek(hoveredDate) : null;
  const endOfHoveredWeek = hoveredDate ? getEndOfWeek(hoveredDate) : null;

  // Handler to clear hoveredDate when mouse leaves the calendar grid
  const handleCalendarMouseLeave = () => {
    setHoveredDate(null);
  };

  return (
    <>
      <TextField
        inputRef={anchorRef}
        value={formatWeekRange(startOfWeek, endOfWeek)}
        onClick={() => setOpen(true)}
        variant="outlined"
        size="small"
        sx={{ mx: 2, minWidth: 260, fontWeight: 600, cursor: 'pointer', background: '#fff' }}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              <CalendarTodayIcon sx={{ color: '#6d7eea' }} />
            </InputAdornment>
          )
        }}
      />
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box sx={{ p: 2 }} onMouseLeave={handleCalendarMouseLeave}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <StaticDatePicker
              displayStaticWrapperAs="desktop"
              value={currentDate}
              onChange={(date) => {
                onWeekChange(getStartOfWeek(date));
                setOpen(false);
              }}
              renderDay={(day, _value, DayComponentProps) => {
                const dayJsDay = dayjs(day);
                const hoveredJs = hoveredDate ? dayjs(hoveredDate) : null;
                const startOfHoveredWeek = hoveredJs ? hoveredJs.startOf('week') : null;
                const endOfHoveredWeek = hoveredJs ? hoveredJs.endOf('week') : null;
                const startOfSelectedWeek = startOfWeek;
                const endOfSelectedWeek = endOfWeek;

                const isSelectedWeek =
                  dayJsDay.isSameOrAfter(startOfSelectedWeek, 'day') &&
                  dayJsDay.isSameOrBefore(endOfSelectedWeek, 'day');
                const isHoveredWeek =
                  hoveredJs &&
                  dayJsDay.isSameOrAfter(startOfHoveredWeek, 'day') &&
                  dayJsDay.isSameOrBefore(endOfHoveredWeek, 'day');

                return (
                  <Box
                    component="span"
                    onMouseEnter={() => setHoveredDate(dayJsDay)}
                    sx={{
                      bgcolor: isSelectedWeek
                        ? '#edeaff'
                        : isHoveredWeek
                        ? '#dbeafe'
                        : undefined,
                      borderRadius: isSelectedWeek || isHoveredWeek ? 0 : 1,
                      transition: 'background 0.2s',
                      display: 'inline-block',
                      width: '40px',
                      height: '40px',
                      margin: isSelectedWeek || isHoveredWeek ? '-2px' : 0,
                      boxSizing: 'border-box',
                    }}
                  >
                    {/* @ts-ignore */}
                    <span {...DayComponentProps} />
                  </Box>
                );
              }}
            />
          </LocalizationProvider>
        </Box>
      </Popover>
    </>
  );
};

const WeekPickerContent = ({ currentDate, onDateChange }) => {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
      <IconButton onClick={() => onDateChange(currentDate.subtract(1, 'week').startOf('week'))}>
        <ArrowBackIosNewIcon fontSize="small" />
      </IconButton>
      <WeekPicker currentDate={currentDate} onWeekChange={onDateChange} />
      <IconButton onClick={() => onDateChange(currentDate.add(1, 'week').startOf('week'))}>
        <ArrowForwardIosIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};

const MonthPickerContent = ({ currentDate, onDateChange }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
    <IconButton onClick={() => onDateChange(currentDate.subtract(1, 'month'))}>
      <ArrowBackIosNewIcon fontSize="small" />
    </IconButton>
    <Box
      sx={{
        mx: 2,
        minWidth: 150,
        fontWeight: 600,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        borderBottom: '1px dashed #6d7eea'
      }}
    >
      <Typography variant="h6">
        {currentDate.format('MMMM YYYY')}
      </Typography>
    </Box>
    <IconButton onClick={() => onDateChange(currentDate.add(1, 'month'))}>
      <ArrowForwardIosIcon fontSize="small" />
    </IconButton>
  </Box>
);

const DateNavigation = ({ 
  view, 
  currentDate, 
  onDateChange,
  datePickerOpen,
  setDatePickerOpen
}) => {
  const renderNavigationContent = () => {
    switch (view) {
      case 'day':
        return (
          <DayPickerContent 
            currentDate={currentDate}
            onDateChange={onDateChange}
            datePickerOpen={datePickerOpen}
            setDatePickerOpen={setDatePickerOpen}
          />
        );
      case 'week':
        return (
          <WeekPickerContent 
            currentDate={currentDate}
            onDateChange={onDateChange}
          />
        );
      case 'month':
        return (
          <MonthPickerContent 
            currentDate={currentDate}
            onDateChange={onDateChange}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
      {renderNavigationContent()}
    </Box>
  );
};

export default DateNavigation; 